import { auth } from '@geo-admin/shared/contract/api/index.ts';
import type { BrandRole, MembershipKind } from '@geo-admin/shared/contract/api/auth.ts';
import type { RouteResponse } from '@geo-admin/shared/contract/route.ts';
import { ROLES, type Role } from '@geo-admin/shared/contract/role.ts';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { DataTable } from '../components/data-table.tsx';
import { Button } from '../components/ui/button.tsx';
import { Badge, Card } from '../components/ui/card.tsx';
import { Dialog } from '../components/ui/dialog.tsx';
import { FieldError, Input, Label, Select } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { dateTime } from '../lib/format.ts';
import { PageTitle } from '../components/PageTitle.tsx';
import { BrandOptions, useBrandChoices } from '../components/BrandKindTabs.tsx';
import { useImpersonate } from '../lib/useImpersonate.ts';
import { useMe } from '../lib/me.ts';

type User = RouteResponse<typeof auth.listUsers>['users'][number];
const ROLE_LABEL: Record<Role, string> = { admin: '운영자', member: '사용자' };
const KIND_LABEL: Record<MembershipKind, string> = { admin: '운영자', brand: '브랜드' };
const BRAND_ROLE_LABEL: Record<BrandRole, string> = { owner: '소유자', manager: '관리자' };

/**
 * 계정 = 로그인 신원, 소속 = 무엇으로 쓰는가 (261013-account-brand-membership). 한 계정이 브랜드 여럿에 소속되고
 * 소속마다 역할(소유자·관리자)이 있다. 운영자 소속은 단독이다(서버가 막는다).
 * 마지막 소유자는 빼거나 내릴 수 없다(409) — 다른 소속자를 먼저 소유자로 올린다
 */

export function UsersPage() {
  const me = useMe();
  const users = useApi(auth.listUsers);
  const reset = useApiMutation(auth.resetUserPassword);
  const disable = useApiMutation(auth.deleteUser);
  const removeMembership = useApiMutation(auth.removeMembership);
  const updateMembership = useApiMutation(auth.updateMembership);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState<User | null>(null);

  /** 대리 로그인 — 절차는 고객 상세와 공유한다(useImpersonate) */
  const impersonate = useImpersonate();

  const columns: ColumnDef<User, unknown>[] = [
    {
      header: '계정',
      accessorKey: 'email',
      cell: ({ row }) => (
        <div className="min-w-0">
          <div>{row.original.email}</div>
          {row.original.name || row.original.companyName ? (
            <div className="text-slate-500 text-xs">
              {[row.original.name, row.original.companyName].filter(Boolean).join(' · ')}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      header: '소속',
      id: 'memberships',
      cell: ({ row }) => {
        const u = row.original;
        const isAdmin = u.memberships.some((m) => m.kind === 'admin');
        return (
          <div className="flex flex-wrap items-center gap-1">
            {u.memberships.length === 0 ? <span className="text-slate-400">소속 없음 — 로그인할 수 없다</span> : null}
            {u.memberships.map((m) => (
              <Badge key={m.id} tone={m.kind === 'admin' ? 'blue' : m.status === 'pending' ? 'amber' : 'slate'}>
                {m.kind === 'admin' ? KIND_LABEL.admin : (m.brandName ?? '')}
                {m.role ? (
                  <button
                    type="button"
                    className="ml-1 underline decoration-dotted"
                    title="역할 바꾸기"
                    onClick={() => {
                      const next = m.role === 'owner' ? 'manager' : 'owner';
                      if (confirm(`${u.email} 을(를) ${m.brandName} 의 ${BRAND_ROLE_LABEL[next]}(으)로 바꿉니다`))
                        updateMembership.mutate(
                          { params: { id: m.id }, body: { role: next } },
                          { onError: (e) => alert(e.message) },
                        );
                    }}
                  >
                    · {BRAND_ROLE_LABEL[m.role]}
                  </button>
                ) : null}
                {m.status === 'pending' ? ' (승인 대기)' : m.status === 'confirmed' ? ' (결제 대기)' : ''}
                {m.kind !== 'admin' ? (
                  <button
                    type="button"
                    className="ml-1 text-slate-400 hover:text-red-600"
                    title="이 소속 빼기 — 남은 소속이 없으면 로그인이 끝난다"
                    onClick={() =>
                      confirm(`${u.email} 의 ${m.brandName} 소속을 뺍니다`) &&
                      removeMembership.mutate({ params: { id: m.id } }, { onError: (e) => alert(e.message) })
                    }
                  >
                    ✕
                  </button>
                ) : null}
              </Badge>
            ))}
            {isAdmin ? null : (
              <Button size="sm" variant="ghost" onClick={() => setAdding(u)}>
                + 소속
              </Button>
            )}
          </div>
        );
      },
    },
    {
      header: '상태',
      id: 'state',
      cell: ({ row }) => (
        <div className="flex gap-1">
          {row.original.mustChangePassword ? <Badge tone="amber">변경 대기</Badge> : null}
          {row.original.lockedUntil ? <Badge tone="red">잠김 ~{dateTime(row.original.lockedUntil)}</Badge> : null}
          {row.original.marketingAgreedAt ? (
            <Badge tone="blue" title={`마케팅 수신 동의 ${dateTime(row.original.marketingAgreedAt)}`}>
              마케팅 동의
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      header: '',
      id: 'actions',
      cell: ({ row }) =>
        row.original.id === me.data?.id ? null : (
          <div className="flex justify-end gap-1">
            {row.original.memberships.some((m) => m.kind === 'brand') ? (
              <Button size="sm" variant="outline" onClick={() => void impersonate(row.original.id)}>
                대리 로그인
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const pw = prompt('임시 비밀번호 (8자 이상) — 본인에게 따로 전달합니다. 모든 세션이 로그아웃됩니다');
                if (pw)
                  reset.mutate(
                    { params: { id: row.original.id }, body: { temporaryPassword: pw } },
                    { onError: (e) => alert(e.message) },
                  );
              }}
            >
              비밀번호 재설정
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                confirm(`${row.original.email} 계정을 비활성화합니다`) &&
                disable.mutate({ params: { id: row.original.id } })
              }
            >
              비활성화
            </Button>
          </div>
        ),
    },
  ];

  return (
    <>
      <PageTitle
        title="계정"
        description="계정은 이메일 하나에 하나입니다. 이미 있는 계정에 브랜드를 더하려면 「+ 소속」을 씁니다(비밀번호는 그대로). 역할을 누르면 소유자·관리자가 바뀝니다. 새 계정은 임시 비밀번호로 만들고 첫 로그인에서 바꾸게 합니다"
        actions={<Button onClick={() => setOpen(true)}>계정 발급</Button>}
      />
      <Card>
        {users.isLoading ? (
          <Loading />
        ) : users.error ? (
          <ErrorBox error={users.error} />
        ) : (
          <DataTable columns={columns} data={users.data?.users ?? []} />
        )}
      </Card>
      <CreateUserDialog open={open} onClose={() => setOpen(false)} />
      <AddMembershipDialog user={adding} onClose={() => setAdding(null)} />
    </>
  );
}

function CreateUserDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [role, setRole] = useState<Role>('member');
  const brands = useBrandChoices(open);
  const create = useApiMutation(auth.createUser);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const f = new FormData(e.currentTarget);
    try {
      await create.mutateAsync({
        body: {
          email: String(f.get('email')),
          role,
          brandId: role === 'member' ? String(f.get('brandId')) : null,
          brandRole: role === 'member' ? (String(f.get('brandRole')) as BrandRole) : 'owner',
          companyName: String(f.get('companyName') ?? '').trim() || null,
          temporaryPassword: String(f.get('temporaryPassword')),
        },
      });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="계정 발급">
      <form className="grid gap-3" onSubmit={submit}>
        <Label>
          이메일
          <Input name="email" type="email" required />
        </Label>
        <Label>
          역할
          <Select value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </Select>
        </Label>
        {role === 'member' ? (
          <>
            <Label>
              첫 브랜드
              <Select name="brandId" required>
                <BrandOptions real={brands.real} test={brands.test} />
              </Select>
            </Label>
            <Label>
              그 브랜드에서의 역할
              <Select name="brandRole" defaultValue="owner">
                <option value="owner">소유자</option>
                <option value="manager">관리자</option>
              </Select>
            </Label>
            <Label>
              회사명 (선택)
              <Input name="companyName" maxLength={120} />
            </Label>
          </>
        ) : null}
        <Label>
          임시 비밀번호 (8자 이상)
          <Input name="temporaryPassword" type="text" minLength={12} required autoComplete="off" />
        </Label>
        <FieldError>{error}</FieldError>
        <Button type="submit">발급</Button>
      </form>
    </Dialog>
  );
}

function AddMembershipDialog({ user, onClose }: { user: User | null; onClose: () => void }) {
  const open = user !== null;
  const brands = useBrandChoices(open);
  const add = useApiMutation(auth.addMembership);
  const [error, setError] = useState<string | null>(null);
  const mine = new Set(user?.memberships.map((m) => m.brandId).filter(Boolean));

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;
    setError(null);
    const f = new FormData(e.currentTarget);
    try {
      await add.mutateAsync({
        params: { id: user.id },
        body: { kind: 'brand', brandId: String(f.get('target')), role: String(f.get('role')) as BrandRole },
      });
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title={`소속 추가 — ${user?.email ?? ''}`}>
      <form className="grid gap-3" onSubmit={submit}>
        <p className="text-slate-500 text-sm">같은 로그인(비밀번호 그대로)으로 그 브랜드도 관리하게 됩니다.</p>
        <Label>
          브랜드
          <Select name="target" required>
            <BrandOptions
              real={brands.real.filter((c) => !mine.has(c.id))}
              test={brands.test.filter((c) => !mine.has(c.id))}
            />
          </Select>
        </Label>
        <Label>
          역할
          <Select name="role" defaultValue="manager">
            <option value="manager">관리자</option>
            <option value="owner">소유자</option>
          </Select>
        </Label>
        <FieldError>{error}</FieldError>
        <Button type="submit" disabled={add.isPending}>
          추가
        </Button>
      </form>
    </Dialog>
  );
}
